import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Card, Container, Grid, Typography } from '@mui/material';
import { ArrowForward } from '@mui/icons-material';
import { Footer, MarketingFinalCta, SectionHeading, SolutionScenarioBoard, ScenarioCheckoutHero } from '../components/homepage';
import { DEMO_REQUEST_MAILTO } from '../config/contact';
import {
  marketingCardSx,
  marketingDisplayFont,
  marketingHeroGridSx,
  marketingMutedSectionSx,
  marketingPalette,
  marketingPrimaryButtonSx,
  marketingSecondaryButtonSx,
  marketingSectionSx,
} from '../components/homepage/marketingSystem';

type TriangleVertex = 'conversion' | 'cost' | 'risk';

type VertexTheme = {
  label: string;
  code: string;
  accent: string;
  soft: string;
  border: string;
  kicker: string;
};

const vertexThemes: Record<TriangleVertex, VertexTheme> = {
  conversion: {
    label: 'Conversion',
    code: 'V-01',
    accent: '#0f6fff',
    soft: 'rgba(15,111,255,0.08)',
    border: 'rgba(15,111,255,0.28)',
    kicker: 'Checkout answer',
  },
  cost: {
    label: 'Payment cost',
    code: 'V-02',
    accent: '#b97a24',
    soft: 'rgba(185,122,36,0.09)',
    border: 'rgba(185,122,36,0.3)',
    kicker: 'Routing economics',
  },
  risk: {
    label: 'Risk control',
    code: 'V-03',
    accent: '#6844c4',
    soft: 'rgba(104,68,196,0.08)',
    border: 'rgba(104,68,196,0.28)',
    kicker: 'Liability & disputes',
  },
};

type PostureLevel = 'primary' | 'held' | 'traded';

type PosturePoint = {
  vertex: TriangleVertex;
  level: PostureLevel;
  descriptor: string;
};



type VerticalCard = {
  id: string;
  name: string;
  loudest: TriangleVertex;
  pain: string[];
  resolution: string;
  posture: PosturePoint[];
};

const industryVerticals: VerticalCard[] = [
  {
    id: 'ecommerce',
    name: 'Ecommerce',
    loudest: 'conversion',
    pain: [
      "Cross-border approval rates lag onshore by 20+ points — method order was set once and never moved.",
      "BNPL lifts conversion above the basket threshold, but only if it's surfaced first for that buyer profile.",
      "3DS posture is one setting for all intents — exemptions applied too broadly, or not at all.",
    ],
    resolution: "Per-intent checkout stack matched to the buyer profile. Wallet-first for repeat mobile, BNPL above threshold, tighter posture on high-risk corridors — each intent gets its own surface.",
    posture: [
      { vertex: 'conversion', level: 'primary', descriptor: 'Per-intent method order lifts approval' },
      { vertex: 'cost', level: 'held', descriptor: 'Small trade only where lift pays it back' },
      { vertex: 'risk', level: 'held', descriptor: 'Posture tightened on exposed corridors only' },
    ],
  },
  {
    id: 'marketplace',
    name: 'Marketplace',
    loudest: 'cost',
    pain: [
      "Multi-corridor payouts compound interchange and FX costs — MID assignments are static while commitment positions shift quarterly.",
      "Flat routing toward the cheapest lane drops approval on complex split-party transactions.",
      "Volume tier thresholds get crossed before the routing layer can react.",
    ],
    resolution: "Live MID assignment recalculated with each transaction as commitment positions shift. Cheapest viable path that still clears conversion and risk targets — per corridor, per call.",
    posture: [
      { vertex: 'conversion', level: 'held', descriptor: 'Approval rate protected on complex routes' },
      { vertex: 'cost', level: 'primary', descriptor: 'MID + route re-authored as positions shift' },
      { vertex: 'risk', level: 'held', descriptor: 'Exposure maintained across seller profiles' },
    ],
  },
  {
    id: 'online-gaming',
    name: 'Online Gaming',
    loudest: 'risk',
    pain: [
      "High-velocity deposit and withdrawal cycles create dispute exposure that's hard to segment from legitimate activity.",
      "Blanket tightening kills conversion for valued players — cohorts that were never risky.",
      "Fraud patterns shift faster than rule-based posture gets updated.",
    ],
    resolution: "Risk posture adjusted by activity profile, velocity signal, and jurisdiction — not applied uniformly. Wallets and lower-compliance rails prioritized where they fit, without flattening the high-value conversion surface.",
    posture: [
      { vertex: 'conversion', level: 'held', descriptor: "Not flattened for valued players" },
      { vertex: 'cost', level: 'held', descriptor: 'Economics on preferred rails maintained' },
      { vertex: 'risk', level: 'primary', descriptor: 'Tightened per profile and velocity signal' },
    ],
  },
  {
    id: 'subscriptions',
    name: 'Subscriptions',
    loudest: 'conversion',
    pain: [
      "Renewal failures create involuntary churn — retry logic is static and doesn't read fresh approval signals.",
      "The best processor for an initial auth isn't always the best for a renewal on an aged card.",
      "Aggressive retry cadences burn out the card relationship or trigger issuer blocks.",
    ],
    resolution: "Renewal strategy tuned to your decline patterns: a different processor where renewals are dying, a different method offer where the card won't clear — and re-tuned as they move.",
    posture: [
      { vertex: 'conversion', level: 'primary', descriptor: 'Renewal approval lifted with fresh routing' },
      { vertex: 'cost', level: 'held', descriptor: 'Economics protected on retry paths' },
      { vertex: 'risk', level: 'held', descriptor: "Issuer relationship not burned by retries" },
    ],
  },
];

type HeroOutcome = {
  vertex: TriangleVertex;
  label: string;
  delta: string;
  direction: 'up' | 'down';
  detail: string;
};

const heroOutcomes: HeroOutcome[] = [
  { vertex: 'conversion', label: 'Conversion', delta: '+1.8%', direction: 'up', detail: 'Cross-border cohort catches up to onshore.' },
  { vertex: 'cost', label: 'Blended cost', delta: '−41 bps', direction: 'down', detail: 'Cheaper rails win when intent clears.' },
  { vertex: 'risk', label: 'Chargeback rate', delta: '−0.12%', direction: 'down', detail: 'Exposure shifts off diluted card flow.' },
];

/**
 * Hero artifact — a solution-oriented visual of what a Ormal recommendation
 * actually produces: the current checkout mix reshaped into an optimized stack,
 * with the outcome deltas on conversion, cost, and risk that come back in the
 * response envelope. Mirrors the fidelity of the real healthcheck output rather
 * than abstracting it to a radar.
 */
const HeroStackShift: React.FC = () => (
  <Box
    sx={{
      position: 'relative',
      border: '1px solid',
      borderColor: 'rgba(196,212,232,0.96)',
      borderRadius: 1.5,
      background: 'linear-gradient(180deg, #ffffff 0%, rgba(246,249,255,1) 100%)',
      boxShadow: '0 28px 70px rgba(15,23,42,0.09)',
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box
      sx={{
        px: { xs: 2, md: 2.3 },
        py: 1.25,
        borderBottom: '1px solid rgba(196,212,232,0.9)',
        backgroundColor: 'rgba(248,251,255,0.95)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        flexWrap: 'wrap',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
        <Box
          sx={{
            width: 7,
            height: 7,
            borderRadius: '50%',
            backgroundColor: '#047857',
            boxShadow: '0 0 10px rgba(4,120,87,0.55)',
          }}
        />
        <Typography sx={{ color: marketingPalette.accent, fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: 400, letterSpacing: '0.04em' }}>
          POST /v1/optimize
        </Typography>
      </Box>
      <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.6 }}>
        <Typography sx={{ color: marketingPalette.muted, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          profile · growth-push
        </Typography>
      </Box>
    </Box>

    {/* Before → After: complete stack comparison, not method-by-method */}
    <Box sx={{ px: { xs: 2, md: 2.3 }, pt: 1.4, pb: 1.2 }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr auto 1fr', gap: 1, alignItems: 'start' }}>
        {/* Current stack */}
        <Box>
          <Typography sx={{ color: marketingPalette.muted, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.7 }}>
            Current stack
          </Typography>
          <Box sx={{ display: 'grid', gap: 0.45 }}>
            {[
              { name: 'Card', sub: 'default · always first' },
              { name: 'PayPal', sub: 'static second' },
              { name: 'Other', sub: 'fixed fallback' },
            ].map((m) => (
              <Box
                key={m.name}
                sx={{
                  px: 0.9, py: 0.65,
                  borderRadius: 0.6,
                  border: '1px solid rgba(196,212,232,0.65)',
                  backgroundColor: 'rgba(248,250,253,0.6)',
                }}
              >
                <Typography sx={{ color: marketingPalette.muted, fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 400, lineHeight: 1.2 }}>
                  {m.name}
                </Typography>
                <Typography sx={{ color: 'rgba(148,163,184,0.65)', fontFamily: 'monospace', fontSize: '0.6rem' }}>
                  {m.sub}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Arrow */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', pt: 2.8 }}>
          <Typography sx={{ color: marketingPalette.accent, fontSize: '1.1rem', fontWeight: 300 }}>›</Typography>
        </Box>

        {/* Ormal stack */}
        <Box>
          <Typography sx={{ color: marketingPalette.accent, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.7 }}>
            Ormal stack
          </Typography>
          <Box sx={{ display: 'grid', gap: 0.45 }}>
            {[
              { name: 'Klarna', sub: 'BNPL · leads for this buyer', leading: true },
              { name: 'Apple Pay', sub: 'wallet · mobile', leading: false },
              { name: 'Card', sub: 'anchor · frictionless', leading: false },
            ].map((m) => (
              <Box
                key={m.name}
                sx={{
                  px: 0.9, py: 0.65,
                  borderRadius: 0.6,
                  border: '1px solid',
                  borderColor: m.leading ? 'rgba(15,111,255,0.32)' : 'rgba(196,212,232,0.7)',
                  backgroundColor: m.leading ? 'rgba(15,111,255,0.07)' : '#fafcff',
                }}
              >
                <Typography sx={{ color: m.leading ? marketingPalette.accent : marketingPalette.headline, fontFamily: 'monospace', fontSize: '0.78rem', fontWeight: 400, lineHeight: 1.2 }}>
                  {m.name}
                </Typography>
                <Typography sx={{ color: m.leading ? 'rgba(15,111,255,0.6)' : marketingPalette.muted, fontFamily: 'monospace', fontSize: '0.6rem' }}>
                  {m.sub}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>

    <Box
      sx={{
        px: { xs: 2, md: 2.3 },
        py: 1.5,
        borderTop: '1px solid rgba(196,212,232,0.9)',
        background: 'linear-gradient(135deg, rgba(15,111,255,0.05), rgba(15,111,255,0.01))',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 1.1 }}>
        <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: '#047857', boxShadow: '0 0 8px rgba(4,120,87,0.7)' }} />
        <Typography sx={{ color: marketingPalette.muted, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
          Outcome · all three balanced · same call
        </Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 0.9 }}>
        {heroOutcomes.map((o) => {
          const theme = vertexThemes[o.vertex];
          const arrow = o.direction === 'up' ? '↑' : '↓';
          return (
            <Box
              key={o.label}
              sx={{
                p: { xs: 1.1, md: 1.3 },
                borderRadius: 0.9,
                border: '1px solid',
                borderColor: theme.border,
                background: `linear-gradient(180deg, ${theme.soft} 0%, rgba(255,255,255,0.6) 100%)`,
                display: 'flex',
                flexDirection: 'column',
                gap: 0.3,
              }}
            >
              <Typography sx={{ color: theme.accent, fontSize: '0.58rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                {o.label}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.4 }}>
                <Typography
                  sx={{
                    color: theme.accent,
                    fontFamily: 'monospace',
                    fontSize: { xs: '1.35rem', md: '1.55rem' },
                    fontWeight: 400,
                    fontVariantNumeric: 'tabular-nums',
                    lineHeight: 1.1,
                    letterSpacing: '-0.02em',
                  }}
                >
                  {o.delta}
                </Typography>
                <Typography sx={{ color: theme.accent, fontSize: '0.72rem', fontWeight: 400, opacity: 0.8 }}>
                  {arrow}
                </Typography>
              </Box>
              <Typography sx={{ color: marketingPalette.body, fontSize: '0.68rem', lineHeight: 1.42 }}>
                {o.detail}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  </Box>
);

const PostureBars: React.FC<{ posture: PosturePoint[] }> = ({ posture }) => {
  const renderBar = (level: PostureLevel, pointTheme: VertexTheme) => {
    const segments = 5;
    const filled = level === 'primary' ? 5 : level === 'held' ? 3 : 2;
    const levelLabel = level === 'primary' ? 'LIFT' : level === 'held' ? 'HELD' : 'TUNED';
    const labelColor = level === 'primary' ? pointTheme.accent : level === 'held' ? '#047857' : '#92400e';
    const barColor = level === 'primary' ? pointTheme.accent : level === 'held' ? '#047857' : '#b45309';
    const barSoft = level === 'primary' ? pointTheme.soft : level === 'held' ? 'rgba(4,120,87,0.12)' : 'rgba(180,83,9,0.12)';
    return { segments, filled, levelLabel, labelColor, barColor, barSoft };
  };
  return (
    <Box sx={{ display: 'grid', gap: 0.75 }}>
      {posture.map((p) => {
        const pointTheme = vertexThemes[p.vertex];
        const { segments, filled, levelLabel, labelColor, barColor, barSoft } = renderBar(p.level, pointTheme);
        return (
          <Box
            key={p.vertex}
            sx={{
              display: 'grid',
              gridTemplateColumns: '54px minmax(0, 1fr)',
              gap: 0.9,
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', backgroundColor: pointTheme.accent, flex: '0 0 auto' }} />
              <Typography
                sx={{
                  color: marketingPalette.headline,
                  fontFamily: 'monospace',
                  fontSize: '0.66rem',
                  fontWeight: 400,
                  letterSpacing: '0.06em',
                }}
              >
                {p.vertex === 'conversion' ? 'CONV' : p.vertex === 'cost' ? 'COST' : 'RISK'}
              </Typography>
            </Box>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.22 }}>
                <Box sx={{ display: 'flex', gap: '3px', flex: '0 0 auto' }}>
                  {Array.from({ length: segments }).map((_, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        width: 9,
                        height: 7,
                        borderRadius: 0.4,
                        background: idx < filled
                          ? `linear-gradient(180deg, ${barColor} 0%, ${barColor}cc 100%)`
                          : barSoft,
                        boxShadow: idx < filled ? `0 0 6px ${barColor}55` : 'none',
                        opacity: idx < filled ? 1 : 0.65,
                      }}
                    />
                  ))}
                </Box>
                <Typography
                  sx={{
                    color: labelColor,
                    fontFamily: 'monospace',
                    fontSize: '0.6rem',
                    fontWeight: 400,
                    letterSpacing: '0.08em',
                  }}
                >
                  {levelLabel}
                </Typography>
              </Box>
              <Typography sx={{ color: marketingPalette.body, fontSize: '0.76rem', lineHeight: 1.42 }}>
                {p.descriptor}
              </Typography>
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};


const Solution: React.FC = () => {
  return (
    <Box className="marketing-layout" sx={{ width: '100%', overflowX: 'hidden' }}>
      <Helmet>
        <link rel="canonical" href="https://ormal.io/solution" />
      </Helmet>
      {/* HERO */}
      <Box component="section" sx={{ ...marketingSectionSx, pt: { xs: 5.5, md: 7.5 }, pb: { xs: 6, md: 7 }, overflow: 'hidden' }}>
        <Box sx={marketingHeroGridSx} />
        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 3.5, lg: 5 }} alignItems="center">
            <Grid item xs={12} lg={6}>
              <SectionHeading
                eyebrow="Solutions"
                title="The right mix of conversion, cost, and risk is different for every business. Ormal finds yours."
                description="Conversion, cost, and risk all matter — the question is what balance is right for your business right now. Ormal executes against your weighting from a single API call, so the stack always reflects your priorities."
                marginBottom={2.5}
              />
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.4 }}>
                <Button component="a" href={DEMO_REQUEST_MAILTO} endIcon={<ArrowForward />} sx={marketingPrimaryButtonSx}>
                  Request Demo
                </Button>
                <Button component={RouterLink} to="/how-it-works" sx={marketingSecondaryButtonSx}>
                  Go under the hood
                </Button>
              </Box>

              {/* Proof strip */}
              <Box
                sx={{
                  mt: 3.2,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: 1.4,
                  width: '100%',
                  maxWidth: 520,
                }}
              >
                {[
                  { value: '+15 pts', label: 'Checkout conversion', detail: 'Luxury resale pilot' },
                  { value: '−8%',     label: 'Blended cost',        detail: 'Multinational marketplace' },
                  { value: 'Same call', label: 'All three balanced', detail: 'Right mix, every transaction' },
                ].map((s) => (
                  <Box
                    key={s.label}
                    sx={{
                      pl: 1.3,
                      borderLeft: '2px solid rgba(15,111,255,0.35)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.2,
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: marketingDisplayFont,
                        fontSize: { xs: '1.05rem', md: '1.18rem' },
                        fontWeight: 400,
                        color: marketingPalette.accent,
                        letterSpacing: '-0.02em',
                        lineHeight: 1.1,
                        fontVariantNumeric: 'tabular-nums',
                      }}
                    >
                      {s.value}
                    </Typography>
                    <Typography sx={{ fontSize: '0.74rem', fontWeight: 400, color: marketingPalette.headline, lineHeight: 1.25 }}>
                      {s.label}
                    </Typography>
                    <Typography sx={{ fontSize: '0.64rem', fontWeight: 400, color: marketingPalette.muted, lineHeight: 1.3 }}>
                      {s.detail}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Grid>
            <Grid item xs={12} lg={6}>
              <HeroStackShift />
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* INDUSTRY VERTICALS */}
      <Box component="section" sx={marketingSectionSx}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="By vertical"
            title="The right mix depends on your business model."
            description="Ecommerce, marketplace, online gaming, subscriptions — each has a different balance that matters most, and a different default route teams take when that balance shifts."
            marginBottom={3}
          />
          <Grid container spacing={2.4}>
            {industryVerticals.map((vertical) => {
              const theme = vertexThemes[vertical.loudest];
              return (
                <Grid item xs={12} md={6} key={vertical.id}>
                  <Card
                    sx={{
                      ...marketingCardSx,
                      height: '100%',
                      p: { xs: 2.2, md: 2.6 },
                      borderTop: `3px solid ${theme.accent}`,
                    }}
                  >
                    {/* Header */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.4 }}>
                      <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: { xs: '1.08rem', md: '1.22rem' }, lineHeight: 1.25 }}>
                        {vertical.name}
                      </Typography>
                      <Box
                        sx={{
                          px: 0.9,
                          py: 0.3,
                          borderRadius: 0.5,
                          border: '1px solid',
                          borderColor: theme.border,
                          backgroundColor: theme.soft,
                        }}
                      >
                        <Typography sx={{ color: theme.accent, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                          {`${theme.label}-weighted`}
                        </Typography>
                      </Box>
                    </Box>

                    {/* Pain signals */}
                    <Box sx={{ mb: 1.6 }}>
                      <Typography sx={{ color: marketingPalette.muted, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.75 }}>
                        Where it shows up
                      </Typography>
                      <Box sx={{ display: 'grid', gap: 0.7 }}>
                        {vertical.pain.map((signal, i) => (
                          <Box key={i} sx={{ display: 'grid', gridTemplateColumns: '20px minmax(0,1fr)', gap: 0.7, alignItems: 'start' }}>
                            <Typography sx={{ color: theme.accent, fontFamily: 'monospace', fontSize: '0.64rem', fontWeight: 400, pt: '3px' }}>
                              {`0${i + 1}`}
                            </Typography>
                            <Typography sx={{ color: marketingPalette.body, fontSize: '0.84rem', lineHeight: 1.55 }}>
                              {signal}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>

                    {/* Ormal's answer */}
                    <Box
                      sx={{
                        p: 1.4,
                        borderRadius: 0.8,
                        border: '1px solid',
                        borderColor: theme.border,
                        backgroundColor: theme.soft,
                        mb: 1.4,
                      }}
                    >
                      <Typography sx={{ color: theme.accent, fontSize: '0.62rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.5 }}>
                        Ormal's answer
                      </Typography>
                      <Typography sx={{ color: marketingPalette.headline, fontSize: '0.87rem', lineHeight: 1.6, fontWeight: 400 }}>
                        {vertical.resolution}
                      </Typography>
                    </Box>

                    {/* Posture */}
                    <Box sx={{ pt: 1.2, borderTop: '1px dashed rgba(196,212,232,0.9)' }}>
                      <Typography sx={{ color: marketingPalette.muted, fontSize: '0.58rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.85 }}>
                        Posture across all 3 axes
                      </Typography>
                      <PostureBars posture={vertical.posture} />
                    </Box>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Container>
      </Box>

      {/* WALK A SCENARIO — worked examples */}
      <Box component="section" sx={{ ...marketingSectionSx, pt: { xs: 3, md: 4 } }}>
        <Container maxWidth="lg">
          <Box
            sx={{
              mb: 2.8,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 2,
              flexWrap: 'wrap',
            }}
          >
            <Box>
              <Typography
                component="span"
                sx={{
                  display: 'block',
                  fontSize: '0.7rem',
                  fontWeight: 500,
                  letterSpacing: '0.12em',
                  textTransform: 'uppercase',
                  color: marketingPalette.accent,
                  lineHeight: 1,
                  mb: 0.8,
                }}
              >
                Worked examples
              </Typography>
              <Typography sx={{ color: marketingPalette.headline, fontWeight: 400, fontSize: { xs: '1.1rem', md: '1.3rem' }, lineHeight: 1.25, letterSpacing: '-0.02em' }}>
                Each user. Each transaction. A completely different payment stack.
              </Typography>
              <Typography sx={{ color: marketingPalette.body, fontSize: '0.9rem', lineHeight: 1.55, mt: 0.85, maxWidth: 620 }}>
                Same API call. Different context and profile — the path, the score, and the runner-up all shift.
              </Typography>
            </Box>
            <Typography sx={{ color: marketingPalette.muted, fontSize: '0.75rem', fontWeight: 400, flexShrink: 0 }}>
              Switch the context below
            </Typography>
          </Box>
          {/* Interactive: four real contexts, request -> composed stack. */}
          <ScenarioCheckoutHero initialScenarioId="cross-border" />
          <Box sx={{ mt: 1.4 }}>
            <SolutionScenarioBoard initialScenarioId="cross-border" />
          </Box>
        </Container>
      </Box>

      {/* WHAT'S COMING — agentic checkout + stablecoin lanes */}
      <Box component="section" sx={marketingMutedSectionSx}>
        <Container maxWidth="lg">
          <SectionHeading
            eyebrow="What's coming through your stack next"
            title="Agentic checkout. Stablecoin lanes. We model both against your data today."
            description="Agentic flows are already showing up at checkout. Stablecoin economics work for some lanes and not others. Both are answered from the same transaction history we already analyze."
            marginBottom={3}
          />
          <Grid container spacing={2.4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ ...marketingCardSx, height: '100%', p: { xs: 2.4, md: 2.8 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.4 }}>
                  <Typography sx={{ color: marketingPalette.accent, fontSize: '0.62rem', fontWeight: 400, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                    Agentic checkout
                  </Typography>
                  <Box sx={{ px: 0.8, py: 0.25, borderRadius: 0.5, border: `1px solid ${marketingPalette.successBorder}`, backgroundColor: marketingPalette.successSoft }}>
                    <Typography sx={{ color: marketingPalette.success, fontSize: '0.56rem', fontWeight: 400, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                      Live now
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ color: marketingPalette.headline, fontSize: { xs: '1.05rem', md: '1.18rem' }, fontWeight: 400, lineHeight: 1.3, mb: 1.2 }}>
                  Most checkout stacks weren't designed for agents.
                </Typography>
                <Typography sx={{ color: marketingPalette.body, lineHeight: 1.7, mb: 1.6, fontSize: '0.92rem' }}>
                  Card requires saved credentials a bot doesn't have. PayPal redirects fail silently for agent flows. Apple Pay needs biometric the agent can't complete. Ormal proposes the agentic-ready stack from your data — account-linked wallets first, network tokens second, fallbacks ranked by initiator-type approval.
                </Typography>
                <Box sx={{ pt: 1.2, borderTop: '1px dashed rgba(196,212,232,0.9)' }}>
                  <Typography sx={{ color: marketingPalette.muted, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.7 }}>
                    What we model from your data
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 0.55 }}>
                    {['PSP approval rates by initiator type', 'Token-based vs. redirect friction at checkout', 'Method fallback rates when the primary path fails'].map((item) => (
                      <Box key={item} sx={{ display: 'grid', gridTemplateColumns: '14px minmax(0,1fr)', gap: 0.7, alignItems: 'start' }}>
                        <Box sx={{ width: 4, height: 4, borderRadius: '50%', backgroundColor: marketingPalette.accent, mt: '0.45rem' }} />
                        <Typography sx={{ color: marketingPalette.body, fontSize: '0.84rem', lineHeight: 1.55 }}>
                          {item}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ ...marketingCardSx, height: '100%', p: { xs: 2.4, md: 2.8 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.4 }}>
                  <Typography sx={{ color: marketingPalette.accent, fontSize: '0.62rem', fontWeight: 400, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                    Stablecoin lanes
                  </Typography>
                  <Box sx={{ px: 0.8, py: 0.25, borderRadius: 0.5, border: '1px solid rgba(15,111,255,0.32)', backgroundColor: 'rgba(15,111,255,0.07)' }}>
                    <Typography sx={{ color: marketingPalette.accent, fontSize: '0.56rem', fontWeight: 400, letterSpacing: '0.12em', textTransform: 'uppercase' }}>
                      Analysis now · routing Q1 2027
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ color: marketingPalette.headline, fontSize: { xs: '1.05rem', md: '1.18rem' }, fontWeight: 400, lineHeight: 1.3, mb: 1.2 }}>
                  Stablecoins make sense on some of your lanes. Not all of them.
                </Typography>
                <Typography sx={{ color: marketingPalette.body, lineHeight: 1.7, mb: 1.6, fontSize: '0.92rem' }}>
                  The answer depends on FX drag, settlement latency cost, and current MID interchange on the high-value lanes specifically. Ormal models the economics lane-by-lane against your transaction data and tells you where the case is strong, where it exists, and where you should wait.
                </Typography>
                <Box sx={{ pt: 1.2, borderTop: '1px dashed rgba(196,212,232,0.9)' }}>
                  <Typography sx={{ color: marketingPalette.muted, fontSize: '0.6rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', mb: 0.7 }}>
                    Where the case lands
                  </Typography>
                  <Box sx={{ display: 'grid', gap: 0.55 }}>
                    {[
                      { signal: 'Strong', detail: 'FX drag exceeds 1.5% on a high-volume lane', accent: '#047857' },
                      { signal: 'Exists', detail: 'Settlement latency is creating working-capital cost', accent: '#b97a24' },
                      { signal: 'Wait', detail: 'Interchange + processor cost beats stablecoin overhead', accent: marketingPalette.muted },
                    ].map((row) => (
                      <Box key={row.signal} sx={{ display: 'grid', gridTemplateColumns: '64px minmax(0,1fr)', gap: 0.9, alignItems: 'start' }}>
                        <Typography sx={{ color: row.accent, fontSize: '0.62rem', fontWeight: 400, letterSpacing: '0.1em', textTransform: 'uppercase', pt: '0.18rem' }}>
                          {row.signal}
                        </Typography>
                        <Typography sx={{ color: marketingPalette.body, fontSize: '0.84rem', lineHeight: 1.55 }}>
                          {row.detail}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* FINAL CTA */}
      <MarketingFinalCta
        kicker="Get started"
        heading="Find out what's in your payment data."
        body="Share as little as 30 days of transaction history — 90 days gives us more signal. One week later you have a quantified opportunity report — or we tell you honestly there isn't one."
        secondary={{ label: 'See how it works', to: '/how-it-works' }}
      />

      <Footer />
    </Box>
  );
};

export default Solution;
